/**
 * Sistema de cache para rascunhos de formulários
 * Salva automaticamente no localStorage e recupera quando necessário
 */

const DEFAULT_EXPIRATION_DAYS = 7

export class DraftCache {
  /**
   * @param {string} cacheKey - Chave única do cache
   * @param {number} expirationDays - Dias até expiração
   */
  constructor(cacheKey, expirationDays = DEFAULT_EXPIRATION_DAYS) {
    this.cacheKey = cacheKey
    this.expirationDays = expirationDays
    this.version = '1.0'
  }

  /**
   * Salva rascunho no localStorage
   */
  save(data, storeUuid) {
    try {
      const now = Date.now()
      const expiresAt = now + (this.expirationDays * 24 * 60 * 60 * 1000)
      
      const draft = {
        version: this.version,
        timestamp: now,
        storeUuid,
        expiresAt,
        data,
      }

      localStorage.setItem(this.cacheKey, JSON.stringify(draft))
      console.log(`✅ Rascunho salvo: ${this.cacheKey}`)
      return true
    } catch (error) {
      console.error('❌ Erro ao salvar rascunho:', error)
      return false
    }
  }

  /**
   * Carrega rascunho do localStorage
   */
  load(storeUuid) {
    try {
      const stored = localStorage.getItem(this.cacheKey)
      if (!stored) return null

      const draft = JSON.parse(stored)
      
      // Verifica expiração
      if (Date.now() > draft.expiresAt) {
        console.log(`⏰ Rascunho expirado: ${this.cacheKey}`)
        this.clear()
        return null
      }

      // Verifica se é da mesma loja
      if (draft.storeUuid !== storeUuid) {
        console.log('🏪 Rascunho de outra loja, ignorando')
        return null
      }

      // Verifica versão (para futuras migrações)
      if (draft.version !== this.version) {
        console.log('📦 Versão incompatível, ignorando')
        return null
      }

      console.log(`✅ Rascunho carregado: ${this.cacheKey}`)
      return draft.data
    } catch (error) {
      console.error('❌ Erro ao carregar rascunho:', error)
      return null
    }
  }

  /**
   * Remove rascunho
   */
  clear() {
    try {
      localStorage.removeItem(this.cacheKey)
      console.log(`🗑️ Rascunho removido: ${this.cacheKey}`)
      return true
    } catch (error) {
      console.error('❌ Erro ao limpar rascunho:', error)
      return false
    }
  }

  /**
   * Verifica se existe rascunho válido
   */
  exists(storeUuid) {
    return this.load(storeUuid) !== null
  }

  /**
   * Retorna idade do rascunho em minutos
   */
  getAge() {
    try {
      const stored = localStorage.getItem(this.cacheKey)
      if (!stored) return null

      const draft = JSON.parse(stored)
      const ageMs = Date.now() - draft.timestamp
      return Math.floor(ageMs / 1000 / 60)
    } catch {
      return null
    }
  }
}

/**
 * Helper para formatar tempo decorrido
 */
export function formatDraftAge(minutes) {
  if (!minutes || minutes < 1) return 'agora mesmo'
  if (minutes < 60) return `há ${minutes} minuto${minutes > 1 ? 's' : ''}`
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `há ${hours} hora${hours > 1 ? 's' : ''}`
  
  const days = Math.floor(hours / 24)
  return `há ${days} dia${days > 1 ? 's' : ''}`
}

/**
 * Limpa rascunhos expirados do localStorage
 */
export function cleanExpiredDrafts() {
  const keysToRemove = []
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      
      if (key && key.startsWith('mc_draft_')) {
        try {
          const draft = JSON.parse(localStorage.getItem(key))
          if (Date.now() > draft.expiresAt) {
            keysToRemove.push(key)
          }
        } catch {
          keysToRemove.push(key)
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key))
    
    if (keysToRemove.length > 0) {
      console.log(`🧹 ${keysToRemove.length} rascunhos expirados removidos`)
    }
  } catch (error) {
    console.error('❌ Erro ao limpar rascunhos expirados:', error)
  }
}
